import {
  Box,
  FormControlLabel,
  RadioGroup as MUIRadioGroup,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { CSSProperties, useMemo } from 'react';

import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { COLORS } from '@/styles/tokens/colors';

import { FormControl } from '../FormControl';
import {
  BaseRadioGroupInputProps,
  FormControlRadioGroupInputProps,
  HelpTextVariant,
  RadioGroupStackingDirection,
} from '../inputTypes';
import { RadioCard } from './RadioCard';
import { StyledRadioButton } from './StyledRadioButton';

export interface RadioGroupProps<V> extends FormControlRadioGroupInputProps<V> {
  label?: string;
  hideLabel?: boolean;
  required?: boolean;
  id?: string;
  contextualHelp?: JSX.Element;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
}

function BaseRadioGroupInput({
  row = false,
  stackingDirection = 'row',
  variant = 'standard',
  size,
  name,
  labelSx,
  sx,
  ...inputProps
}: BaseRadioGroupInputProps<string>) {
  // Strip error since radio group does not support errors
  const { error, disabled, inputRef, options, columnSpacing, ...rest } =
    inputProps;

  const styleForStackingDirection: Record<
    RadioGroupStackingDirection,
    CSSProperties
  > = {
    row: {},
    column: {
      display: 'grid',
      gridTemplateColumns: `repeat(${options.length}, 1fr)`,
    },
  };

  const sxForFormControlLabel: SxProps<Theme> = useMemo(() => {
    const sxForStackingDirection = {
      row: {},
      column: {
        alignItems: 'center',
      },
    };

    const baseLabelSx = Object.assign({}, sx ?? {});

    return {
      ...sxForStackingDirection[stackingDirection],
      ...Object.assign(baseLabelSx, labelSx),
    };
  }, [sx, labelSx, stackingDirection]);

  return (
    <>
      <MUIRadioGroup
        {...rest}
        sx={sx}
        ref={inputRef}
        row={row}
        value={inputProps.value}
        onChange={inputProps.onChange}
        style={styleForStackingDirection[stackingDirection]}
      >
        <Stack
          spacing={row ? undefined : columnSpacing}
          direction={row ? 'row' : 'column'}
          sx={
            variant === 'card'
              ? {
                  gap: 1.5,
                  ...(row && { flexDirection: { xs: 'column', sm: 'row' } }),
                }
              : undefined
          }
        >
          {options.map((option, idx) => {
            const isSelected = option.value === inputProps.value;
            const identifier = ['radio', name, option.value, idx]
              .filter((n) => Boolean(n) || n === 0)
              .join('-');
            if (variant === 'card') {
              return (
                <Box key={identifier} sx={{ flex: 1, minWidth: 0 }}>
                  <RadioCard
                    id={identifier}
                    value={option.value}
                    size={size}
                    label={option.label}
                    helpText={option.helpText}
                    contextualHelp={option.contextualHelp}
                    disabled={disabled || option.disabled}
                    sx={sxForFormControlLabel}
                  />
                  {isSelected && option.selectedOptionAdornment}
                </Box>
              );
            }
            return (
              <Box key={identifier}>
                <FormControlLabel
                  sx={sxForFormControlLabel}
                  value={option.value}
                  control={<StyledRadioButton id={identifier} size={size} />}
                  htmlFor={identifier}
                  label={
                    // the entirety of this component is already wrapped in a `label` element via the use of FormControlLabel,
                    // so this typography element component should be a `span` even though the variant is `label`
                    <>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          component="span"
                          variant={size === 'medium' ? 'body-s' : 'body-xs'}
                          color={COLORS.TEXT.PRIMARY}
                        >
                          {option.label}
                        </Typography>
                        {option.contextualHelp && (
                          <ContextualHelpTooltip>
                            {option.contextualHelp}
                          </ContextualHelpTooltip>
                        )}
                      </Box>
                      {option.helpText && (
                        <Typography display="block" variant="body-xs">
                          {option.helpText}
                        </Typography>
                      )}
                    </>
                  }
                  disabled={disabled || option.disabled}
                />
                {isSelected && option.selectedOptionAdornment}
              </Box>
            );
          })}
        </Stack>
      </MUIRadioGroup>
    </>
  );
}

export function RadioGroup<V>({
  id,
  label,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  hideLabel,
  required,
  ...inputProps
}: RadioGroupProps<V>) {
  return (
    <FormControl<BaseRadioGroupInputProps<V>>
      id={id}
      hideLabel={hideLabel}
      contextualHelp={contextualHelp}
      component={BaseRadioGroupInput}
      errorMessage={errorMessage}
      inputProps={inputProps}
      label={label}
      required={required}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
    />
  );
}
